/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * The draft/value translation behind {@link ListField}, kept pure so the rule that actually
 * matters — **a separator the operator has typed survives until they type after it** — is testable
 * without a DOM.
 *
 * This is `NumberField`'s defect in another type, and it was shipped on 2026-08-28 in `XS1`'s
 * `is one of` control: the value was parsed on every keystroke and rendered back from the parsed
 * list, so typing `kids,` produced `['kids']` which rendered as `kids` — **the comma was deleted
 * as it was typed**, and a list of more than one member was unauthorable. Reported by Todd within
 * a day of it shipping, exactly as the numeric one was.
 *
 * The rule generalises and is worth stating once: **a control whose text and value are different
 * shapes must own the text.** Round-tripping the operator's keystrokes through the parsed value
 * destroys every intermediate state the parser does not have a representation for — `''` for a
 * number, a trailing separator for a list.
 */

/** How a list from the parent renders as text. `undefined` and `[]` are an empty box. */
export function listToDraft(value: readonly (string | number | boolean)[] | undefined): string {
  if (!value || value.length === 0) return '';
  return value.map((v) => String(v)).join(', ');
}

/**
 * What the operator's text means.
 *
 * Members are trimmed and blanks dropped, so `kids,` is `['kids']` and `a, , b` is `['a','b']` —
 * which is what makes the trailing comma harmless *as a value* while the draft keeps it on screen.
 *
 * **A member containing a comma is not representable**, and that is stated rather than discovered:
 * the alternative is a chip control, and `X4` replaces this slot with a full value editor, so a
 * second widget built now is one built to be thrown away.
 */
export function parseListDraft(draft: string): string[] {
  return draft
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

/**
 * Should the parent's value overwrite the text on screen?
 *
 * Only on a real disagreement. `"kids, "` already *means* `['kids']`, so re-syncing on it would
 * delete the separator the instant it was typed — the same interference `shouldResyncDraft` exists
 * to prevent for `2000.`.
 */
export function shouldResyncListDraft(
  draft: string,
  value: readonly (string | number | boolean)[] | undefined,
): boolean {
  const parsed = parseListDraft(draft);
  const target = (value ?? []).map((v) => String(v));
  if (parsed.length !== target.length) return true;
  return parsed.some((member, i) => member !== target[i]);
}
